import { z } from "zod";
/**
 * Player identity contracts (phase plan S2-2).
 *
 * The nickname rules live here so the SPA validates exactly what the server
 * validates — the design spec's `invalid` field state is a client-side check
 * of the same rule, not a guess at it.
 */
export const NICKNAME_MIN_LENGTH = 3;
export const NICKNAME_MAX_LENGTH = 20;
export const nicknameSchema = z
    .string()
    .trim()
    .min(NICKNAME_MIN_LENGTH)
    .max(NICKNAME_MAX_LENGTH)
    .regex(/^[A-Za-z0-9_]+$/, "letters, numbers and underscores only");
export const playerRoleSchema = z.enum(["guest", "registered", "admin"]);
/**
 * What the client is allowed to know about the signed-in player.
 *
 * Note what is absent: `authUserId`, the email address, suspension reasons.
 * The client gets an identity, not a database row (architecture §9 tier 3).
 */
export const playerProfileSchema = z.object({
    id: z.string().uuid(),
    role: playerRoleSchema,
    nickname: z.string(),
    /** Always absolute on the wire, even though it may be stored relative. */
    avatarUrl: z.string(),
    isGuest: z.boolean(),
    /**
     * Server's answer to "may this player re-roll or pick an avatar?". The UI
     * renders the guest variant from this rather than inferring it from `role`,
     * so the rule has exactly one owner.
     */
    canChangeIdentity: z.boolean(),
    /**
     * Has this player been through the mandatory `/welcome` identity gate
     * (PRD §4.4)?
     *
     * The splash's routing matrix (design spec §5) branches on it, and it is a
     * *server* answer for the same reason `canChangeIdentity` is: a gate the
     * client decides it has passed is a gate that a private window skips.
     */
    identityConfirmed: z.boolean(),
    /**
     * The caller's own coin balance — **S4-8's authoritative read**.
     *
     * `null` means "no claim is being made", never zero (Ivy §9.2, LOCKED): a
     * guest with a real, empty wallet sees `0`; a client with no balance source
     * sees `—`. Substituting one for the other is a lie about a number.
     *
     * ── Why it is on the profile rather than on a route of its own ─────────────
     *
     * The live number arrives over the socket, per player, and the socket can miss
     * an event — a drop, a Redis blip, a room that had not subscribed yet. §S4-8's
     * rule is that a resubscribe reconciles with **one** authoritative read, and
     * this is it: `GET /api/v1/players/me` is already the session bootstrap, it is
     * per-caller and uncached by construction, and it is available on every screen
     * including the ones with no socket at all. A second route would be a second
     * answer to "what does this player have".
     *
     * It is the same `getBalance()` the lobby's `viewer.balanceCoins` reads, so
     * the two cannot disagree — one truth, two carriers.
     */
    balanceCoins: z.number().int().nullable(),
    createdAt: z.string(),
});
export const avatarOptionSchema = z.object({
    id: z.string().uuid(),
    url: z.string(),
    name: z.string(),
});
export const avatarListResponseSchema = z.object({
    avatars: z.array(avatarOptionSchema),
});
export const nicknameAvailabilityQuerySchema = z.object({
    nickname: z.string().min(1).max(64),
});
/**
 * **LOCKED (design spec §9, PRD §4.4): a taken nickname and a screened
 * nickname produce the identical response.** Both are
 * `{ available: false, suggestions: [...] }` with no `reason`. The only
 * populated `reason` is `invalid`, which reports a rule the client could have
 * checked itself and therefore leaks nothing.
 *
 * Quinn: if you can tell profanity from collision by diffing two responses,
 * that is the bug. They must differ in nothing — not a field, not a status
 * code, not the number of suggestions.
 */
export const nicknameAvailabilityResponseSchema = z.object({
    nickname: z.string(),
    available: z.boolean(),
    reason: z.literal("invalid").optional(),
    rule: z.enum(["length", "charset"]).optional(),
    suggestions: z.array(z.string()),
});
export const updateNicknameRequestSchema = z.object({
    nickname: z.string().min(1).max(64),
});
export const updateAvatarRequestSchema = z.object({
    avatarId: z.string().uuid(),
});
export const nicknameSuggestionResponseSchema = z.object({
    nickname: z.string(),
});
// ---- guest → registered conversion (phase plan S2-3) ------------------------
/**
 * Must match Better Auth's `emailAndPassword.minPasswordLength` /
 * `maxPasswordLength` in `apps/api/src/auth/auth.ts`. They are here so the
 * sign-up form and the conversion sheet enforce the same rule the server does
 * — the client check is a courtesy, the server's is the rule.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
/**
 * `POST /api/v1/players/me/convert` — a sign-up wearing a different frame
 * (design spec §14). The player id is NOT in the body: it comes from the
 * session, because the one thing a conversion must never accept from a client
 * is which account to convert.
 */
export const convertGuestRequestSchema = z.object({
    email: z.string().trim().toLowerCase().pipe(z.email()),
    password: passwordSchema,
    /**
     * The design spec's §14 ToS checkbox, as a required literal `true`.
     *
     * A boolean the server merely *records* would be consent theatre — the
     * client could send `false` and still get an account. `z.literal(true)` makes
     * the absent or false case a 400 from the schema, before any handler runs,
     * and `players.tos_accepted_at` is stamped by the conversion transaction
     * itself (never from a timestamp the client supplied).
     *
     * Sign-up has the same gate, enforced in Better Auth's `before` hook —
     * `apps/api/src/auth/auth.ts`.
     */
    tosAccepted: z.literal(true),
    /**
     * Where the player was when they opened the sheet (design spec §14.1 r1).
     *
     * Optional, advisory, and validated again server-side before it goes
     * anywhere: its one job is to travel into the verification mail's callback so
     * the email-link road out of the success panel lands where the panel's CTA
     * road does (QA T-175 N2). The schema is loose on purpose — a stale or odd
     * value must produce the default landing, never a failed conversion, so
     * there is no shape rule here for it to fail. The API's `safeReturnPath`
     * decides what survives (length included) and drops the rest silently.
     */
    next: z.string().optional(),
});
/**
 * The converted identity, plus what the client must do next.
 *
 * `verificationRequired` is always true today (`requireEmailVerification`), but
 * the client branches on the field rather than on the assumption, so turning
 * verification off some day is a config change and not a client bug.
 */
export const convertGuestResponseSchema = z.object({
    player: playerProfileSchema,
    email: z.string(),
    verificationRequired: z.boolean(),
});
/**
 * `details.reason` on a failed conversion. The client renders a different panel
 * for `email_in_use` (PRD §4.5: "log in to that account instead"), so the
 * reason is part of the contract rather than a log line.
 */
export const CONVERSION_FAILURE_REASONS = [
    "email_in_use",
    "not_a_guest",
    "unavailable_account",
    "rejected",
    "conflict",
];
export const conversionFailureReasonSchema = z.enum(CONVERSION_FAILURE_REASONS);
//# sourceMappingURL=players.js.map